/**
 * Dot plots: one stacked dot per observation.
 *
 * Without a bin width every distinct value gets its own stack — the "line plot" of
 * school statistics. With one, values are grouped into bins [a, b) of that width (the
 * last bin closed, as in a histogram) and each bin's dots are stacked over its centre,
 * which is Wilkinson's dot plot for data too spread out to stack value by value.
 */

import {
  niceStep,
  niceTicks,
  roundTo,
  stepDecimals,
} from "@/lib/tool-kit/charts/scale";

export interface DotStack {
  /** Where the stack sits on the axis: the value, or the bin's centre. */
  position: number;
  count: number;
  /** Bin edges, when binned. */
  start: number | null;
  end: number | null;
}

/** Values that differ only by binary noise (0.1 + 0.2 vs 0.3) share a stack. */
function valueKey(value: number): number {
  return roundTo(value, 10);
}

export function dotStacks(
  values: readonly number[],
  binWidth: number | null = null
): DotStack[] {
  if (values.length === 0) {
    return [];
  }
  if (!(binWidth && binWidth > 0)) {
    const counts = new Map<number, number>();
    for (const value of values) {
      const key = valueKey(value);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort(([a], [b]) => a - b)
      .map(([position, count]) => ({
        position,
        count,
        start: null,
        end: null,
      }));
  }
  const decimals = Math.min(12, stepDecimals(binWidth) + 1);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const start = roundTo(Math.floor(min / binWidth + 1e-9) * binWidth, decimals);
  const bins = Math.max(1, Math.ceil((max - start) / binWidth - 1e-9));
  const counts = new Array<number>(bins).fill(0);
  for (const value of values) {
    const index = Math.min(
      bins - 1,
      Math.floor((value - start) / binWidth + 1e-9)
    );
    counts[index] += 1;
  }
  const stacks: DotStack[] = [];
  for (const [index, count] of counts.entries()) {
    if (count === 0) {
      continue;
    }
    const low = roundTo(start + index * binWidth, decimals);
    const high = roundTo(start + (index + 1) * binWidth, decimals);
    stacks.push({
      position: roundTo((low + high) / 2, decimals + 1),
      count,
      start: low,
      end: high,
    });
  }
  return stacks;
}

/** The smallest gap between neighbouring stacks — how much room each column has. */
export function smallestGap(stacks: readonly DotStack[]): number | null {
  let gap: number | null = null;
  for (let i = 1; i < stacks.length; i++) {
    const distance = stacks[i].position - stacks[i - 1].position;
    if (distance > 0 && (gap === null || distance < gap)) {
      gap = distance;
    }
  }
  return gap;
}

export interface DotAxis {
  /** The number line's ends, half a step beyond the outer ticks. */
  min: number;
  max: number;
  ticks: number[];
  step: number;
  /** Label ticks as fractions over this denominator, or null for decimals. */
  denominator: number | null;
  decimals: number;
}

/** Most ticks on a dot plot's number line before the step doubles. */
const MAX_DOT_TICKS = 24;
/** Fractions finer than sixteenths are labelled as decimals. */
const MAX_DENOMINATOR = 16;

function stepTicks(low: number, high: number, step: number): number[] {
  const decimals = Math.min(12, stepDecimals(step) + 1);
  const ticks: number[] = [];
  const first = Math.floor(low / step + 1e-9);
  const last = Math.ceil(high / step - 1e-9);
  for (let i = first; i <= last; i++) {
    ticks.push(roundTo(i * step, decimals) || 0);
  }
  return ticks;
}

/**
 * The number line under a dot plot. Whole numbers get a tick at every value (a school
 * line plot labels each one); fractions get ticks at 1/denominator steps; a binned
 * plot gets its bin edges. The step doubles until there are at most 24 ticks.
 */
export function dotPlotAxis(
  stacks: readonly DotStack[],
  {
    denominator = null,
    binWidth = null,
  }: { denominator?: number | null; binWidth?: number | null } = {}
): DotAxis {
  const low = stacks[0]?.start ?? stacks[0]?.position ?? 0;
  const high = stacks.at(-1)?.end ?? stacks.at(-1)?.position ?? 1;
  if (binWidth && binWidth > 0) {
    let step = binWidth;
    while ((high - low) / step > MAX_DOT_TICKS) {
      step *= 2;
    }
    const ticks = stepTicks(low, high, step);
    return {
      min: low,
      max: high,
      ticks,
      step,
      denominator: null,
      decimals: stepDecimals(step),
    };
  }
  const integers = stacks.every((stack) => Number.isInteger(stack.position));
  const fraction =
    denominator !== null &&
    denominator > 1 &&
    denominator <= MAX_DENOMINATOR &&
    !integers;
  let step: number;
  if (fraction) {
    step = 1 / denominator;
  } else if (integers) {
    step = 1;
  } else {
    step = niceTicks(low, high, { count: 8 }).step;
  }
  while ((high - low) / step > MAX_DOT_TICKS) {
    step = fraction || integers ? step * 2 : niceStep(high - low, 8);
  }
  const ticks = stepTicks(low, high, step);
  return {
    min: (ticks[0] ?? low) - step / 2,
    max: (ticks.at(-1) ?? high) + step / 2,
    ticks,
    step,
    denominator: fraction ? denominator : null,
    decimals: fraction ? 0 : stepDecimals(step),
  };
}
